/**
 * Custom hook for user autocomplete
 * 
 * Provides debounced autocomplete suggestions for users from Elasticsearch
 */
import { useState } from 'react'
import { useDebouncedValue } from '../../../hooks/useDebouncedValue'
import { useQuery } from '@tanstack/react-query'
import { v1Api } from '../../../api/generatedClient'
import { getApiBaseUrl } from '../../../api/authTokens'
import type { APIUserAutocompleteResponse } from '../../../generated-types/models/APIUserAutocompleteResponse'
import type { V1SearchAccountsUsersAutocompleteRetrieveRequest } from '../../../generated-types/apis/V1Api'

export interface UserSuggestion {
  id: string
  email: string
  full_name: string
  role_names: string[]
  root_account_name: string
  self: string
}

export interface UserAutocompleteResponse {
  suggestions: UserSuggestion[]
  total: number
}

/**
 * Fetches user autocomplete suggestions
 */
const fetchUserAutocomplete = async (
  query: string,
  staffOnly = false,
  size = 10
): Promise<UserAutocompleteResponse> => {
  const response: APIUserAutocompleteResponse = await v1Api.v1SearchAccountsUsersAutocompleteRetrieve({
    q: query || undefined,
    size,
    staffOnly,
  } as V1SearchAccountsUsersAutocompleteRetrieveRequest)

  const baseUrl = getApiBaseUrl()

  return {
    suggestions: response.suggestions.map(suggestion => ({
      id: suggestion.id,
      email: suggestion.email,
      full_name: suggestion.fullName,
      role_names: suggestion.roleNames,
      root_account_name: suggestion.rootAccountName,
      self: `${baseUrl}v1/accounts/users/${suggestion.id}/`,
    })),
    total: response.total,
  }
}

/**
 * Hook for user autocomplete
 * 
 * @param options - Configuration options
 * @returns Autocomplete hook result with suggestions and loading state
 * 
 * @example
 * ```tsx
 * const { suggestions, isLoading, updateQuery } = useUserAutocomplete({ staffOnly: true })
 * 
 * <input onChange={(e) => updateQuery(e.target.value)} />
 * {suggestions.map(s => <option key={s.id} value={s.id}>{s.full_name || s.email}</option>)}
 * ```
 */
export function useUserAutocomplete(
  options: {
    staffOnly?: boolean
    size?: number
    enabled?: boolean
  } = {}
) {
  const { staffOnly = false, size = 10, enabled = true } = options
  const [query, setQuery] = useState('')
  
  const debouncedQuery = useDebouncedValue(query, 300)

  const { data, isLoading, error } = useQuery<UserAutocompleteResponse, Error>({
    queryKey: ['user-autocomplete', debouncedQuery, staffOnly],
    queryFn: () => fetchUserAutocomplete(debouncedQuery, staffOnly, size),
    enabled: enabled && (debouncedQuery.length >= 2 || debouncedQuery === ''),
    staleTime: 30000,
    retry: 1,
    refetchOnWindowFocus: false,
  })

  return {
    suggestions: data?.suggestions ?? [],
    total: data?.total ?? 0,
    isLoading: isLoading && (debouncedQuery.length >= 2 || debouncedQuery === ''),
    error,
    updateQuery: setQuery,
    query,
  }
}


